/* import __COLOCATED_TEMPLATE__ from './getting-started-container.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';
import { type Step } from 'embercom/objects/onboarding/checklist/step';
import type GreatGuidanceService from 'embercom/services/great-guidance-service';
import type IntlService from 'embercom/services/intl';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { showNewMessageInIntercomWidget } from 'embercom/lib/intercom-widget-helper';
import { restartableTask } from 'ember-concurrency-decorators';
import { get } from 'embercom/lib/ajax';
import type { TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type RouterService from '@ember/routing/router-service';
import { SHOPIFY_ONBOARDING_GUIDE, SALES_AGENT_ONBOARDING_GUIDE } from 'embercom/lib/home/guides';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import {
  StandalonePlatformDisplayName,
  type StandalonePlatform,
} from 'embercom/objects/standalone/constants';
import type CompellingOfferCampaignService from 'embercom/services/compelling-offer-campaign-service';

interface Args {}

interface Signature {
  Args: Args;
  Element: never;
}

export const StepIdentifier = {
  SETUP_OMNICHANNEL: 'great_guidance_setup_omnichannel',
  ADD_SUPPORT_CONTENT: 'great_guidance_add_support_content',
  INVITE_TEAMMATES: 'great_guidance_invite_teammates',
  SETUP_ARTICLES: 'great_guidance_setup_articles',
  SETUP_FIN_AGENT: 'great_guidance_setup_fin_agent',
  SETUP_FIN_AGENT_VELOCITY_MOTION: 'great_guidance_setup_fin_agent_velocity_motion',
  PROACTIVE_SUPPORT: 'great_guidance_proactive_support',
  SETUP_FIN_COPILOT: 'great_guidance_setup_fin_copilot',
  SETUP_WORKFLOWS: 'great_guidance_setup_workflows',
  // FFO AI shared steps (used across multiple archetypes)
  AI_GUIDE_SETUP_CHANNELS: 'great_guidance_ai_guide_setup_channels',
  AI_GUIDE_INVITE_TEAMMATES: 'great_guidance_ai_guide_invite_teammates',
  // FFO AI Innovator steps
  AI_INNOVATOR_TRAIN_FIN_WITH_CONTENT: 'great_guidance_ai_innovator_train_fin_with_content',
  AI_INNOVATOR_GUIDE_FIN_TO_RESPOND: 'great_guidance_ai_innovator_guide_fin_to_respond',
  AI_INNOVATOR_TEST_FIN_ANSWERS: 'great_guidance_ai_innovator_test_fin_answers',
  AI_INNOVATOR_SETUP_CHANNELS: 'great_guidance_ai_innovator_setup_channels',
  AI_INNOVATOR_DEPLOY_FIN: 'great_guidance_ai_innovator_deploy_fin',
  AI_INNOVATOR_INVITE_TEAMMATES: 'great_guidance_ai_innovator_invite_teammates',
  AI_INNOVATOR_TRAIN_FIN_WITH_ZENDESK_CONTENT:
    'great_guidance_ai_innovator_train_fin_with_zendesk_content',
  // FFO AI Explorer steps
  AI_EXPLORER_ADD_CONTENT_TO_POWER_FIN: 'great_guidance_ai_explorer_add_content_to_power_fin',
  AI_EXPLORER_GUIDE_FIN_TO_RESPOND: 'great_guidance_ai_explorer_guide_fin_to_respond',
  AI_EXPLORER_CREATE_WORKFLOWS_CONTROL_FIN:
    'great_guidance_ai_explorer_create_workflows_control_fin',
  AI_EXPLORER_DEPLOY_FIN: 'great_guidance_ai_explorer_deploy_fin',
  AI_EXPLORER_ADD_ZENDESK_CONTENT_TO_POWER_FIN:
    'great_guidance_ai_explorer_add_zendesk_content_to_power_fin',
  // FFO AI Skeptic steps
  AI_SKEPTIC_ADD_CONTENT_TO_POWER_HELP_CENTER:
    'great_guidance_ai_skeptic_add_content_to_power_help_center',
  AI_SKEPTIC_CREATE_WORKFLOWS_AUTOMATE_TASKS:
    'great_guidance_ai_skeptic_create_workflows_automate_tasks',
  AI_SKEPTIC_LEARN_ABOUT_FIN: 'great_guidance_ai_skeptic_learn_about_fin',
  AI_SKEPTIC_TEST_FIN_WITH_SUPPORT_CONTENT:
    'great_guidance_ai_skeptic_test_fin_with_support_content',
  AI_SKEPTIC_ADD_ZENDESK_CONTENT_TO_POWER_HELP_CENTER:
    'great_guidance_ai_skeptic_add_zendesk_content_to_power_help_center',
  SHOPIFY_ONBOARDING_INVITE_TEAMMATES: 'shopify_onboarding_invite_teammates',
  SHOPIFY_ONBOARDING_SETUP_CHANNELS: 'shopify_onboarding_setup_channels',
  SHOPIFY_ONBOARDING_TRAIN_FIN_WITH_ZENDESK_CONTENT:
    'shopify_onboarding_train_fin_with_zendesk_content',
  SHOPIFY_ONBOARDING_INSTALL_MESSENGER: 'shopify_onboarding_install_messenger',
  // Sales Agent Onboarding steps
  SALES_AGENT_ONBOARDING_INSTALL_MESSENGER: 'sales_agent_onboarding_install_messenger',
  SALES_AGENT_ONBOARDING_SETUP_FIN_SALES_AGENT: 'sales_agent_onboarding_setup_fin_sales_agent',
  SALES_AGENT_ONBOARDING_INVITE_TEAMMATES: 'sales_agent_onboarding_invite_teammates',
  SALES_AGENT_ONBOARDING_TRAIN_FIN_WITH_ZENDESK_CONTENT:
    'sales_agent_onboarding_train_fin_with_zendesk_content',
} as const;

export type StepIdentifier = (typeof StepIdentifier)[keyof typeof StepIdentifier];

const STEP_TO_WISTIA_MAP: Record<StepIdentifier, string> = {
  [StepIdentifier.SETUP_OMNICHANNEL]: 'd15xapp07h',
  [StepIdentifier.ADD_SUPPORT_CONTENT]: 'caromk1jsx',
  [StepIdentifier.INVITE_TEAMMATES]: '1wpkv0in60',
  [StepIdentifier.SETUP_ARTICLES]: 'h6vf3agoor',
  [StepIdentifier.SETUP_FIN_AGENT]: '80xp9psptk',
  [StepIdentifier.SETUP_FIN_AGENT_VELOCITY_MOTION]: '80xp9psptk',
  [StepIdentifier.PROACTIVE_SUPPORT]: 'r1n3e8vygg',
  [StepIdentifier.SETUP_FIN_COPILOT]: 'rtteugoyfk',
  [StepIdentifier.SETUP_WORKFLOWS]: '8uuvjql7ux',
  // FFO AI shared steps (used across multiple archetypes)
  [StepIdentifier.AI_GUIDE_SETUP_CHANNELS]: 'd15xapp07h',
  [StepIdentifier.AI_GUIDE_INVITE_TEAMMATES]: '1wpkv0in60',
  // FFO AI Innovator steps
  [StepIdentifier.AI_INNOVATOR_TRAIN_FIN_WITH_CONTENT]: 'gyansjp1ed',
  [StepIdentifier.AI_INNOVATOR_GUIDE_FIN_TO_RESPOND]: 'yaq9q0v9kh',
  [StepIdentifier.AI_INNOVATOR_TEST_FIN_ANSWERS]: '7aua3ifiek',
  [StepIdentifier.AI_INNOVATOR_SETUP_CHANNELS]: 'd15xapp07h',
  [StepIdentifier.AI_INNOVATOR_DEPLOY_FIN]: 'himeo67t47',
  [StepIdentifier.AI_INNOVATOR_INVITE_TEAMMATES]: '1wpkv0in60',
  [StepIdentifier.AI_INNOVATOR_TRAIN_FIN_WITH_ZENDESK_CONTENT]: 'gyansjp1ed',
  // FFO AI Explorer steps
  [StepIdentifier.AI_EXPLORER_ADD_CONTENT_TO_POWER_FIN]: 'caromk1jsx',
  [StepIdentifier.AI_EXPLORER_GUIDE_FIN_TO_RESPOND]: 'yaq9q0v9kh',
  [StepIdentifier.AI_EXPLORER_CREATE_WORKFLOWS_CONTROL_FIN]: '8uuvjql7ux',
  [StepIdentifier.AI_EXPLORER_DEPLOY_FIN]: 'himeo67t47',
  [StepIdentifier.AI_EXPLORER_ADD_ZENDESK_CONTENT_TO_POWER_FIN]: 'caromk1jsx',
  // FFO AI Skeptic steps
  [StepIdentifier.AI_SKEPTIC_ADD_CONTENT_TO_POWER_HELP_CENTER]: 'h6vf3agoor',
  [StepIdentifier.AI_SKEPTIC_CREATE_WORKFLOWS_AUTOMATE_TASKS]: '8uuvjql7ux',
  [StepIdentifier.AI_SKEPTIC_LEARN_ABOUT_FIN]: '80xp9psptk',
  [StepIdentifier.AI_SKEPTIC_TEST_FIN_WITH_SUPPORT_CONTENT]: '7aua3ifiek',
  [StepIdentifier.AI_SKEPTIC_ADD_ZENDESK_CONTENT_TO_POWER_HELP_CENTER]: 'h6vf3agoor',
  [StepIdentifier.SHOPIFY_ONBOARDING_INVITE_TEAMMATES]: '1wpkv0in60',
  [StepIdentifier.SHOPIFY_ONBOARDING_SETUP_CHANNELS]: 'd15xapp07h',
  [StepIdentifier.SHOPIFY_ONBOARDING_TRAIN_FIN_WITH_ZENDESK_CONTENT]: 'gyansjp1ed',
  [StepIdentifier.SHOPIFY_ONBOARDING_INSTALL_MESSENGER]: '883eqxmxjo',
  // Sales Agent Onboarding steps
  [StepIdentifier.SALES_AGENT_ONBOARDING_INSTALL_MESSENGER]: 'd15xapp07h',
  [StepIdentifier.SALES_AGENT_ONBOARDING_SETUP_FIN_SALES_AGENT]: '',
  [StepIdentifier.SALES_AGENT_ONBOARDING_INVITE_TEAMMATES]: '1wpkv0in60',
  [StepIdentifier.SALES_AGENT_ONBOARDING_TRAIN_FIN_WITH_ZENDESK_CONTENT]: 'gyansjp1ed',
};

export default class GettingStartedContainer extends Component<Signature> {
  @service declare intercomEventService: any;
  @service declare realTimeEventService: any;
  @service declare appService: AppService;
  @service declare onboardingHomeService: $TSFixMe;
  @service declare customerService: $TSFixMe;
  @service declare greatGuidanceService: GreatGuidanceService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare router: RouterService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare compellingOfferCampaignService: CompellingOfferCampaignService;

  @tracked resetModalOpen = false;
  @tracked resetSurveyModalOpen = false;
  @tracked leadSurveyCompletedByOtherAdmin = false;
  @tracked fadeIn = true;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.intercomEventService.trackAnalyticsEvent({
      action: 'viewed',
      object: 'getting_started',
      context: 'getting_started_homepage',
      section: 'onboarding.getting-started',
      place: 'onboarding.getting-started',
    });

    this.addNexusEventListeners();
  }

  @restartableTask
  *checkReferral(): TaskGenerator<any> {
    try {
      let isReferred = yield get(`/ember/referrals/is_referred?app_id=${this.appId}`);
      if (isReferred.is_referred) {
        this.notificationsService.notifyConfirmation(
          this.intl.t('onboarding.getting-started.referral-notification', {
            referredByName: isReferred.referred_by,
          }),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }

  get appId() {
    return this.appService.app.id;
  }

  willDestroy() {
    super.willDestroy();
    this.removeNexusEventListeners();
  }

  addNexusEventListeners() {
    this.realTimeEventService.subscribeTopics(['mark-step-as-completed']);
    this.realTimeEventService.on('MarkStepAsCompleted', this, 'markStepCompleted');
  }

  removeNexusEventListeners() {
    this.realTimeEventService.unsubscribeTopics(['mark-step-as-completed']);
    this.realTimeEventService.off('MarkStepAsCompleted', this, 'markStepCompleted');
  }

  get totalSteps() {
    return this.steps.length;
  }

  get titleFontClass() {
    if (this.isFinFirstOnboardingSurvey) {
      return 'onboarding__getting-started__fin-first-title';
    }
    return 'text-editorial-h2';
  }

  get completedSteps() {
    return this.steps.filter((step: any) => step.completed).length;
  }

  get currentAssetUrl() {
    let currentStepId = this.greatGuidanceService.currentStepId;
    if (
      currentStepId === 'fin_sa_onboarding_add_integration' ||
      currentStepId === 'fin_sa_onboarding_connect_channels_and_turn_fin_live'
    ) {
      return assetUrl(
        `/assets/images/onboarding/getting-started/${currentStepId}_${this.platformKey}.png`,
      );
    }
    return assetUrl(`/assets/images/onboarding/getting-started/${currentStepId}.png`);
  }

  get wistiaId(): string {
    let stepId = this.greatGuidanceService.currentStepId as StepIdentifier;
    if (!stepId) {
      return '';
    }
    return STEP_TO_WISTIA_MAP[stepId];
  }

  get blogUrl() {
    return 'https://www.intercom.com/blog/';
  }

  get helpCenterUrl() {
    if (this.appService.app.isStandaloneApp) {
      return 'https://fin.ai/help/';
    }
    return 'https://www.intercom.com/help/';
  }

  get showOnboardingHeroBanner() {
    let metadata = this.compellingOfferCampaignService.freeSeatsCampaignMetadata;
    let isFreeSeatsCampaign = this.compellingOfferCampaignService.isFreeSeatsCampaign;

    // Show banner if the phase is in trial and user is in the free seats campaign
    return metadata?.subscriptionPhase === 'in_trial' && isFreeSeatsCampaign;
  }

  get showProfilingSurvey() {
    return this.appService.app.canSeeProfilingSurvey && !this.leadSurveyCompletedByOtherAdmin;
  }

  get steps(): any[] {
    return this.greatGuidanceService.steps;
  }

  get nextStep(): Step | undefined {
    if (this.greatGuidanceService.nextStepId) {
      let nextStep = this.steps.find(
        (step) => step.identifier === this.greatGuidanceService.nextStepId,
      );
      if (nextStep && nextStep.state === 'available') {
        return this.steps.find((step) => step.identifier === this.greatGuidanceService.nextStepId);
      }
    }

    return this.steps.find((step) => step.state === 'available');
  }

  markStepCompleted(event: any) {
    let step = this.steps.find((step: any) => step.identifier === event.step_identifier);
    if (step) {
      step.state = 'completed';
      this.greatGuidanceService.nextStepId = undefined;
      this.greatGuidanceService.currentStepId = this.greatGuidanceService.steps.find(
        (step: any) => step.state === 'available',
      )?.identifier;
      this.intercomEventService.trackAnalyticsEvent({
        action: 'completed',
        context: 'getting_started_homepage',
        object: 'step_completed',
        step_identifier: `${step.identifier}`,
        section: 'onboarding.getting-started',
        place: 'onboarding.getting-started',
      });
    }
  }

  @action
  async onSurveyCompletion() {
    await this.onboardingHomeService.resetGuide();
    await this.greatGuidanceService.fetchSteps(this.appService.app.id);
    this.greatGuidanceService.nextStepId = undefined;
    this.greatGuidanceService.currentStepId = this.nextStep?.identifier;
    taskFor(this.checkReferral).perform();
  }

  @action
  updateLeadSurveyCompletionStatus(completedAdminId: number) {
    this.leadSurveyCompletedByOtherAdmin =
      parseInt(this.appService.app.currentAdmin.id, 10) !== completedAdminId;
  }

  @action onOpenSectionChange(sectionId: string) {
    this.fadeIn = false;
    if (this.greatGuidanceService.currentStepId === sectionId) {
      this.greatGuidanceService.currentStepId = undefined;
    } else {
      setTimeout(() => {
        this.fadeIn = true;
        this.greatGuidanceService.currentStepId = sectionId;
      }, 300);
      this.intercomEventService.trackAnalyticsEvent({
        action: 'opened',
        context: 'getting_started_homepage',
        object: `${sectionId}-step`,
        section: 'onboarding.getting-started',
        place: 'onboarding.getting-started',
      });
    }
  }

  @action
  triggerAnalyticsForGuideCard(card_id: string, article_id?: number) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'getting_started_guide_card',
      section: 'onboarding.getting-started',
      place: 'onboarding.getting-started',
      context: `getting_started_homepage.guide-cards.${card_id}`,
      article_id,
    });
  }

  @action toggleResetModal() {
    this.resetModalOpen = !this.resetModalOpen;
  }

  @action
  toggleResetSurveyModal() {
    this.resetSurveyModalOpen = !this.resetSurveyModalOpen;
  }

  get isZendeskPlatform() {
    return this.platformKey === 'zendesk';
  }

  get canUseReactStepBasedIA() {
    return this.finStandaloneService.platform.canUseReactStepBasedIA;
  }

  get platform() {
    return StandalonePlatformDisplayName[this.platformKey];
  }

  get platformKey(): StandalonePlatform {
    return this.finStandaloneService.platform.platformKey;
  }

  get isFinFirstOnboardingSurvey() {
    return this.greatGuidanceService.guide?.identifier === 'fin_first_onboarding_guidance';
  }

  get isShopifyOnboardingGuide() {
    return this.greatGuidanceService.guide?.identifier === SHOPIFY_ONBOARDING_GUIDE;
  }

  get isSalesAgentOnboardingGuide() {
    return this.greatGuidanceService.guide?.identifier === SALES_AGENT_ONBOARDING_GUIDE;
  }

  get isAiInnovator() {
    return this.greatGuidanceService.steps.some((step) =>
      step.identifier.startsWith('great_guidance_ai_innovator'),
    );
  }

  get isAiExplorer() {
    return this.greatGuidanceService.steps.some((step) =>
      step.identifier.startsWith('great_guidance_ai_explorer'),
    );
  }

  get isAiSkeptic() {
    return this.greatGuidanceService.steps.some((step) =>
      step.identifier.startsWith('great_guidance_ai_skeptic'),
    );
  }

  get bestFromAiAutomationGuideCardContent(): {
    title: string;
    description: string;
    assetUrl: string;
    onCardClick: () => void;
  } {
    let defaultGuideCardContent = {
      title: this.intl.t('onboarding.getting-started.guides.guide-1.default.title'),
      description: this.intl.t('onboarding.getting-started.guides.guide-1.default.description'),
      assetUrl: assetUrl('/assets/images/onboarding/home/guide-card/guide-card-image-1.png'),
      onCardClick: () => {
        window.Intercom('showArticle', 9638929);
        this.triggerAnalyticsForGuideCard('get-best-from-ai-Automation', 9638929);
      },
    };

    if (!this.isFinFirstOnboardingSurvey) {
      return defaultGuideCardContent;
    }

    if (this.isAiInnovator) {
      return {
        title: this.intl.t('onboarding.getting-started.guides.guide-1.ai-innovator.title'),
        description: this.intl.t(
          'onboarding.getting-started.guides.guide-1.ai-innovator.description',
        ),
        assetUrl: assetUrl(
          '/assets/images/onboarding/home/guide-card/guide-1-fin-first-onboarding.png',
        ),
        onCardClick: () => {
          this.router.transitionTo('apps.app.automation.fin-ai-agent.tutorials');
          this.triggerAnalyticsForGuideCard('get-best-from-ai-Automation');
        },
      };
    }

    if (this.isAiExplorer) {
      return {
        title: this.intl.t('onboarding.getting-started.guides.guide-1.ai-explorer.title'),
        description: this.intl.t(
          'onboarding.getting-started.guides.guide-1.ai-explorer.description',
        ),
        assetUrl: assetUrl(
          '/assets/images/onboarding/home/guide-card/guide-1-fin-first-onboarding.png',
        ),
        onCardClick: () => {
          this.router.transitionTo('apps.app.automation.fin-ai-agent.tutorials');
          this.triggerAnalyticsForGuideCard('get-best-from-ai-Automation');
        },
      };
    }

    if (this.isAiSkeptic) {
      return {
        title: this.intl.t('onboarding.getting-started.guides.guide-1.ai-skeptic.title'),
        description: this.intl.t(
          'onboarding.getting-started.guides.guide-1.ai-skeptic.description',
        ),
        assetUrl: assetUrl(
          '/assets/images/onboarding/home/guide-card/guide-1-fin-first-onboarding.png',
        ),
        onCardClick: () => {
          window.Intercom('showArticle', 9638929);
          this.triggerAnalyticsForGuideCard('get-best-from-ai-Automation', 9638929);
        },
      };
    }

    return defaultGuideCardContent;
  }

  get improveCustomerExperienceGuideCardAssetUrl(): string {
    if (this.isFinFirstOnboardingSurvey) {
      return assetUrl('/assets/images/onboarding/home/guide-card/guide-2-fin-first-onboarding.png');
    }
    return assetUrl('/assets/images/onboarding/home/guide-card/guide-card-image-2.png');
  }

  @action
  openChatToFinSupport() {
    showNewMessageInIntercomWidget();
  }

  @action
  openLinkInNewTab(url: string) {
    safeWindowOpen(url, '_blank');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Onboarding::GettingStarted::GettingStartedContainer': typeof GettingStartedContainer;
  }
}
